import React, { useEffect } from "react";
import { useLocation } from "react-router-dom";

import Header from "../../../components/Header";
import Footer from "../../../components/Footer";
import "../styleAllBlogs.css";

import lawnAeration from "../../../assets/img/lawn-aeration-equipment-burhan.jpg";
import fertilizerSpreader from "../../../assets/img/lawn-maintenance-fertilizer-spreader.jpg";

export default function SpringLawnCareChecklistBlog() {
  const location = useLocation();

  useEffect(() => {
    const canonicalUrl = `https://bur-han.ca${location.pathname}`;
    const linkTag = document.querySelector('link[rel="canonical"]');
    if (linkTag) {
      linkTag.setAttribute("href", canonicalUrl);
    } else {
      const newLinkTag = document.createElement("link");
      newLinkTag.setAttribute("rel", "canonical");
      newLinkTag.setAttribute("href", canonicalUrl);
      document.head.appendChild(newLinkTag);
    }

    const newTitle = "Spring Lawn Care Checklist for Greater Vancouver | BUR-HAN";
    document.title = newTitle;

    const newDescription =
      "Complete spring lawn care checklist for Vancouver, North Vancouver, West Vancouver, Burnaby & Surrey. Aeration, fertilizing, moss control, overseeding — everything your lawn needs after winter.";
    const descriptionMetaTag = document.querySelector('meta[name="description"]');
    if (descriptionMetaTag) {
      descriptionMetaTag.setAttribute("content", newDescription);
    } else {
      const newMetaTag = document.createElement("meta");
      newMetaTag.setAttribute("name", "description");
      newMetaTag.setAttribute("content", newDescription);
      document.head.appendChild(newMetaTag);
    }

    const newKeywords = [
      "spring lawn care Vancouver, spring lawn checklist BC, spring lawn tips Vancouver, spring lawn care North Vancouver, spring lawn care Burnaby, lawn care spring Surrey, spring garden care Vancouver, spring aeration Vancouver, spring fertilizing Vancouver, spring overseeding BC, spring moss control Vancouver, lawn recovery spring Vancouver",
    ].join(", ");
    const keywordsMetaTag = document.querySelector('meta[name="keywords"]');
    if (keywordsMetaTag) {
      keywordsMetaTag.setAttribute("content", newKeywords);
    } else {
      const newMetaTag = document.createElement("meta");
      newMetaTag.setAttribute("name", "keywords");
      newMetaTag.setAttribute("content", newKeywords);
      document.head.appendChild(newMetaTag);
    }

    const ogTitleTag = document.querySelector('meta[property="og:title"]');
    if (ogTitleTag) {
      ogTitleTag.setAttribute("content", newTitle);
    } else {
      const newOgTitleTag = document.createElement("meta");
      newOgTitleTag.setAttribute("property", "og:title");
      newOgTitleTag.setAttribute("content", newTitle);
      document.head.appendChild(newOgTitleTag);
    }

    const ogDescriptionTag = document.querySelector('meta[property="og:description"]');
    if (ogDescriptionTag) {
      ogDescriptionTag.setAttribute("content", newDescription);
    } else {
      const newOgDescriptionTag = document.createElement("meta");
      newOgDescriptionTag.setAttribute("property", "og:description");
      newOgDescriptionTag.setAttribute("content", newDescription);
      document.head.appendChild(newOgDescriptionTag);
    }
  }, [location.pathname]);

  return (
    <div className="wrapper">
      <Header />
      <div className="beforeSection"></div>
      <div className="sectionBlogpost">
        <a href="/blog" className="backPosts">
          <i className="bi bi-arrow-left"></i>
          Back
        </a>
        <article>
          <header>
            <h1>
              <div className="nth-line-1" data-aos="fade-up">
                Spring Lawn Care Checklist
              </div>
              <div className="nth-line-2" data-aos="fade-up">
                for Greater Vancouver Homeowners
              </div>
            </h1>
            <p className="lead" data-aos="fade-right">
              Everything your lawn needs in March, April, and May to recover from winter and thrive through summer
            </p>
          </header>
          <hr />
          <section data-aos="fade-right">
            <p>
              Spring is the most important season for lawn recovery in the Lower Mainland. After months of wet weather, low light, and minimal growth, Vancouver lawns need a targeted set of treatments to wake up strong and build the density and root depth that carries them through summer. Miss the spring window and you'll spend the rest of the year playing catch-up.
              <br /><br />
              At BUR-HAN Garden &amp; Lawn Care, here's the spring checklist we follow for lawns across Vancouver, North Vancouver, West Vancouver, Burnaby, Surrey, Coquitlam, Richmond, and surrounding areas.
            </p>

            <h2>Step 1: Assess Your Lawn After Winter</h2>
            <p>
              Before doing anything else, walk your property and note the damage. Look for:
              <br /><br />
              — <strong>Moss coverage</strong> — common in shaded or acidic areas of Lower Mainland lawns
              <br />
              — <strong>Bare or thin patches</strong> — often from chafer beetle damage, heavy foot traffic, or winter fungal disease
              <br />
              — <strong>Compaction</strong> — heavy rainfall through winter compacts clay-heavy soils common in the Vancouver area
              <br />
              — <strong>Thatch buildup</strong> — a layer of dead organic matter at the soil surface that can block water and nutrients
              <br /><br />
              Your assessment tells you which of the following steps are most urgent for your specific lawn.
            </p>

            <img loading="lazy"
              src={lawnAeration}
              alt="Spring core aeration service for Vancouver lawns — BUR-HAN Garden and Lawn Care"
              title="Spring Lawn Aeration in Vancouver"
              width={"90% !important"}
            />

            <h2>Step 2: Core Aeration (March–April)</h2>
            <p>
              Spring <a href="/blog/aerating-your-lawn-how-to-do-it-right-in-vancouver" style={{ color: "var(--yellow-color)" }}>core aeration</a> is one of the highest-impact things you can do for a Vancouver lawn. It relieves the compaction built up through the wet winter, opens channels for water and nutrients to reach roots, and creates an ideal seedbed for overseeding.
              <br /><br />
              <strong>Best timing in Vancouver:</strong> Late February to April, once the soil has dried enough that cores come out cleanly (typically 2–3 inches deep). Aerating when soil is too wet leads to poor core extraction and compaction from the equipment.
              <br /><br />
              Leave the cores on the surface — they break down within 2–4 weeks and return nutrients to the soil.
            </p>

            <h2>Step 3: Moss Treatment and Raking (March–April)</h2>
            <p>
              Apply an iron sulfate-based moss treatment in early spring while moss is still actively growing. The product turns moss black within a week as it kills it. Wait 2–3 weeks, then rake out the dead moss before overseeding.
              <br /><br />
              Do not skip the raking step. Dead moss left on the surface creates a physical barrier that prevents seed-to-soil contact during overseeding and blocks water from reaching grass roots.
              <br /><br />
              For more on permanent moss control, read our guide: <a href="/blog/moss-control-vancouver-lawns" style={{ color: "var(--yellow-color)" }}>Moss Control in Vancouver Lawns</a>.
            </p>

            <h2>Step 4: Overseeding Bare and Thin Areas (March–May)</h2>
            <p>
              After aeration and moss removal, overseed any bare, thin, or damaged patches. Spring and fall are both excellent seeding windows in Vancouver's climate — germination is reliable with cool temperatures and consistent moisture.
              <br /><br />
              <strong>Seed selection tips for the Lower Mainland:</strong>
              <br />
              — Use a mix containing perennial ryegrass and fine fescue for most Vancouver residential lawns
              <br />
              — For heavily shaded areas (common in North Vancouver and West Vancouver), choose a shade-tolerant fescue blend
              <br />
              — Keep seeded areas consistently moist for the first 2–3 weeks until germination is complete
            </p>

            <img loading="lazy"
              src={fertilizerSpreader}
              alt="Applying spring fertilizer to a Vancouver lawn with professional spreader"
              title="Spring Lawn Fertilizing in Vancouver"
              width={"90% !important"}
            />

            <h2>Step 5: Spring Fertilizing (March–May)</h2>
            <p>
              Once soil temperatures rise above 8°C and you see consistent new growth, apply your first fertilizer of the season. A balanced formula higher in nitrogen — such as 20-5-10 — supports rapid green-up and encourages dense, upright growth.
              <br /><br />
              Do not fertilize too early. Applying to cold, saturated soil in February leads to nutrient runoff rather than absorption. Wait for consistent growth signals before fertilizing.
              <br /><br />
              For a full year-round fertilizing schedule, see our guide: <a href="/blog/when-to-fertilize-lawn-vancouver" style={{ color: "var(--yellow-color)" }}>When to Fertilize Your Lawn in Vancouver</a>.
            </p>

            <h2>Step 6: Lime Application (March–May or Fall)</h2>
            <p>
              Vancouver's heavy rainfall creates naturally acidic soil — often too acidic for grass to thrive. Annual liming raises soil pH, making nutrients more available to grass roots and making conditions less hospitable to moss.
              <br /><br />
              Apply pelletized lime in spring or fall. It works slowly over several months, so don't expect immediate results — consistent annual application over 2–3 years gradually corrects soil pH and produces lasting improvement.
            </p>

            <h2>Step 7: Resume Mowing on the Right Schedule</h2>
            <p>
              As growth picks up in March and April, resume your regular mowing schedule. For Vancouver lawns in spring:
              <br /><br />
              — Start at a slightly lower height for the first cut of the season to remove any dead tips and encourage upright growth
              <br />
              — From late March onward, mow at 2–2.5 inches
              <br />
              — Mow weekly as growth accelerates through April and May
              <br />
              — Never remove more than one-third of the blade length in a single cut
              <br /><br />
              Consistent mowing at the right height is one of the most effective ways to crowd out moss and weeds while encouraging dense, healthy turf.
            </p>

            <p>
              BUR-HAN Garden &amp; Lawn Care provides complete spring lawn care programs across Greater Vancouver, including aeration, moss control, overseeding, fertilizing, and lime application. Our crews begin spring services in February and March — book early to secure your spot in the schedule.
              <br /><br />
              <a href="/contact-us" style={{ color: "var(--yellow-color)" }}>Contact us for a free spring lawn assessment.</a>
            </p>
          </section>
        </article>
        <div className="relatedArticles">
          <h3>Related Articles</h3>
          <ul>
            <li><a href="/blog/aerating-your-lawn-how-to-do-it-right-in-vancouver">Aerating Your Lawn: Why, When, and How to Do It Right in Vancouver</a></li>
            <li><a href="/blog/organic-vs-synthetic-fertilizer-vancouver">Organic vs Synthetic Fertilizer: What's Best for Vancouver Lawns?</a></li>
            <li><a href="/blog/how-often-mow-lawn-vancouver">How Often Should You Mow Your Lawn in Vancouver?</a></li>
            <li><a href="/blog/moss-control-vancouver-lawns">Moss Control in Vancouver Lawns: Causes, Treatment & Prevention</a></li>
          </ul>
        </div>
        <div className="secondBanner">
          <div className="container" data-aos="fade-down">
            <div className="row">
              <div className="col-md-6">
                <h3>Ready for a great spring lawn?</h3>
                <p>
                  Book your spring lawn care program early — aeration, moss control, overseeding, and fertilizing across Greater Vancouver.
                </p>
              </div>
              <div className="col-md-6">
                <div className="call-to-action text-right">
                  <a href="/contact-us" className="get-a-quote">
                    Book Spring Services
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
}
